/**
 * Speedin Subscribe & Save Storefront Plugin Runtime (V2 — Pay-Per-Delivery + Prepaid)
 * - Mounts interactive Subscribe & Save selectors in Liquid sections
 * - Supports both Pay-per-delivery (30, 45, 60 days) and Prepaid (3-month, 6-month) plans
 * - Syncs `input[name="selling_plan"]`, `properties[Delivery Frequency]`, and `properties[Billing Type]`
 * - Exposes `window.SpeedinSubscriptionPlugin` for the Full-Motion React runtime & Cart Drawer
 */
(() => {
  if (window.SpeedinSubscriptionPlugin) return;

  const DEFAULT_DISCOUNT_PCT = 15;
  const FREQUENCIES = [30, 45, 60];
  const SUPR_SELLING_PLAN_IDS = {};

  window.SpeedinSubscriptionPlans = window.SpeedinSubscriptionPlans || {};
  for (const [h, pid] of Object.entries(SUPR_SELLING_PLAN_IDS)) {
    if (!window.SpeedinSubscriptionPlans[h]) {
      window.SpeedinSubscriptionPlans[h] = { 30: pid, 45: pid, 60: pid, default: pid };
    }
  }

  function getDiscountedCents(baseCents, discountPct = DEFAULT_DISCOUNT_PCT) {
    return Math.floor((baseCents * (100 - discountPct)) / 100 + 1e-6);
  }

  function formatMoney(cents) {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  function resolveAllocationId(allocations, frequencyDays) {
    if (!Array.isArray(allocations) || allocations.length === 0) return '';
    const match = allocations.find((a) => String(a.name || '').includes(String(frequencyDays)));
    return String((match || allocations[0]).id || '');
  }

  function syncProductForm(widget, mode, frequencyDays, billingType, sellingPlanId) {
    const section = widget.closest('.shopify-section, section, form') || document;
    const form =
      widget.closest('form[action*="/cart/add"]') ||
      section.querySelector('form[action*="/cart/add"], .sp-product-form');
    if (!form) return;

    let planInput = form.querySelector('input[name="selling_plan"], select[name="selling_plan"]');
    if (!planInput) {
      planInput = document.createElement('input');
      planInput.type = 'hidden';
      planInput.name = 'selling_plan';
      form.appendChild(planInput);
    }
    planInput.value = mode === 'subscribe' ? sellingPlanId || '' : '';

    let freqProp = form.querySelector('input[name="properties[Delivery Frequency]"]');
    if (!freqProp) {
      freqProp = document.createElement('input');
      freqProp.type = 'hidden';
      freqProp.name = 'properties[Delivery Frequency]';
      form.appendChild(freqProp);
    }
    freqProp.value =
      mode === 'subscribe'
        ? billingType === 'prepaid'
          ? `Prepaid (${Math.round(frequencyDays / 30)} months · Ships monthly)`
          : `Every ${frequencyDays} days`
        : '';

    let billingProp = form.querySelector('input[name="properties[Billing Type]"]');
    if (!billingProp) {
      billingProp = document.createElement('input');
      billingProp.type = 'hidden';
      billingProp.name = 'properties[Billing Type]';
      form.appendChild(billingProp);
    }
    billingProp.value = mode === 'subscribe' ? billingType : 'one-time';

    let modeProp = form.querySelector('input[name="properties[_purchase_mode]"]');
    if (!modeProp) {
      modeProp = document.createElement('input');
      modeProp.type = 'hidden';
      modeProp.name = 'properties[_purchase_mode]';
      form.appendChild(modeProp);
    }
    modeProp.value = mode;
  }

  function initWidget(widget) {
    if (widget.dataset.spSubInitialized === 'true') return;
    widget.dataset.spSubInitialized = 'true';

    let planData = { allocations: [], basePriceCents: Number(widget.dataset.basePrice || 2999) };
    const jsonEl = widget.querySelector('[data-sp-selling-plans]');
    if (jsonEl) {
      try {
        planData = { ...planData, ...JSON.parse(jsonEl.textContent || '{}') };
      } catch {
        /* ignore */
      }
    }

    let mode = widget.dataset.defaultMode || 'subscribe';
    let billingType = 'pay-per-delivery';
    let frequencyDays = Number(widget.dataset.defaultFrequency || 30);
    let discountPct = Number(widget.dataset.discountPct || DEFAULT_DISCOUNT_PCT);

    const optionButtons = widget.querySelectorAll('[data-sp-sub-option]');
    const billingTabs = widget.querySelectorAll('[data-sp-billing-tab]');
    const paygGroup = widget.querySelector('[data-sp-payg-group]');
    const prepaidGroup = widget.querySelector('[data-sp-prepaid-group]');
    const freqButtons = widget.querySelectorAll('[data-sp-sub-freq]');
    const detailsEl = widget.querySelector('[data-sp-sub-details]');
    const subPriceEl = widget.querySelector('[data-sp-sub-price]');
    const subBadgeEl = widget.querySelector('[data-sp-sub-badge]');
    const freqHeadingEl = widget.querySelector('[data-sp-freq-heading]');
    const freqHintEl = widget.querySelector('[data-sp-freq-hint]');

    function render() {
      optionButtons.forEach((btn) => {
        const active = btn.dataset.spSubOption === mode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-checked', String(active));
      });

      billingTabs.forEach((tab) => {
        const active = tab.dataset.spBillingTab === billingType;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });

      if (paygGroup) paygGroup.hidden = billingType !== 'pay-per-delivery';
      if (prepaidGroup) prepaidGroup.hidden = billingType !== 'prepaid';

      freqButtons.forEach((pill) => {
        const selected =
          Number(pill.dataset.spSubFreq) === frequencyDays &&
          (pill.dataset.spBillingType || 'pay-per-delivery') === billingType;
        pill.classList.toggle('is-selected', selected);
        if (selected && pill.dataset.spDiscount) {
          discountPct = Number(pill.dataset.spDiscount);
        }
      });

      const unitSubCents = getDiscountedCents(planData.basePriceCents, discountPct);
      if (subPriceEl) {
        subPriceEl.textContent =
          billingType === 'prepaid' ? `${formatMoney(unitSubCents)}/mo` : formatMoney(unitSubCents);
      }
      if (subBadgeEl) {
        subBadgeEl.textContent = `SAVE ${discountPct}%`;
      }
      if (freqHeadingEl) {
        freqHeadingEl.textContent =
          billingType === 'prepaid' ? 'Prepaid bundle duration' : 'Delivery frequency';
      }
      if (freqHintEl) {
        const cycles = Math.max(1, Math.round(frequencyDays / 30));
        freqHintEl.textContent =
          billingType === 'prepaid'
            ? `Ships monthly · Billed ${formatMoney(unitSubCents * cycles)}`
            : 'Auto-renews · Change anytime';
      }

      if (detailsEl) {
        detailsEl.hidden = mode !== 'subscribe';
      }
      const planId = resolveAllocationId(planData.allocations, frequencyDays);
      syncProductForm(widget, mode, frequencyDays, billingType, planId);

      document.dispatchEvent(
        new CustomEvent('speedin:subscription:change', {
          detail: {
            handle: widget.dataset.productHandle,
            mode,
            billingType,
            frequencyDays,
            discountPct,
            sellingPlanId: planId ? Number(planId) : undefined,
          },
        }),
      );
    }

    optionButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        if (
          e.target.closest('[data-sp-sub-freq]') ||
          e.target.closest('[data-sp-billing-tab]')
        ) {
          return;
        }
        mode = btn.dataset.spSubOption || 'subscribe';
        render();
      });
    });

    billingTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        e.stopPropagation();
        mode = 'subscribe';
        billingType = tab.dataset.spBillingTab || 'pay-per-delivery';
        frequencyDays = billingType === 'prepaid' ? 90 : 30;
        render();
      });
    });

    freqButtons.forEach((pill) => {
      pill.addEventListener('click', (e) => {
        e.stopPropagation();
        mode = 'subscribe';
        billingType = pill.dataset.spBillingType || 'pay-per-delivery';
        frequencyDays = Number(pill.dataset.spSubFreq || 30);
        render();
      });
      pill.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          mode = 'subscribe';
          billingType = pill.dataset.spBillingType || 'pay-per-delivery';
          frequencyDays = Number(pill.dataset.spSubFreq || 30);
          render();
        }
      });
    });

    render();
  }

  function boot(scope = document) {
    scope.querySelectorAll('[data-sp-subscription-plugin]').forEach(initWidget);
  }

  window.SpeedinSubscriptionPlugin = {
    version: '2.0.0',
    frequencies: FREQUENCIES,
    defaultDiscountPct: DEFAULT_DISCOUNT_PCT,
    getDiscountedCents,
    boot,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
